import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ProductCard from '@/components/base/ProductCard';
import WhatsAppFloat from '@/components/feature/WhatsAppFloat';
import PageHero from '@/components/feature/PageHero';
import SectionHeading from '@/components/base/SectionHeading';
import CTABanner from '@/components/feature/CTABanner';
import FAQAccordion from '@/components/base/FAQAccordion';
import { desks } from '@/mocks/products';
import CuradoriaGaleria from '@/components/feature/CuradoriaGaleria';
import { corporativo } from '@/mocks/curadoria';
import { useSEO } from '@/hooks/useSEO';

const faqs = [
  { q: 'Quais tipos de mesas corporativas a Home Office Premium oferece em Caruaru?', a: 'Oferecemos mesas executivas, estações de trabalho em L, mesas de reunião, mesas operacionais e mesas para open office da marca Marzo Vitorino. Atendemos empresas em Caruaru, Recife, Petrolina, Garanhuns e todo Pernambuco.' },
  { q: 'A montagem das mesas corporativas está inclusa no preço?', a: 'Sim. A montagem e instalação das mesas corporativas está inclusa em todos os projetos da Home Office Premium. Nossa equipe técnica realiza a montagem no local, garantindo perfeito acabamento e alinhamento.' },
  { q: 'Qual a garantia das mesas Marzo Vitorino?', a: 'As mesas Marzo Vitorino possuem garantia de fábrica de 5 anos contra defeitos de fabricação. A Home Office Premium oferece suporte técnico pós-venda em Caruaru e região.' },
  { q: 'A Home Office Premium entrega mesas corporativas em Recife e Petrolina?', a: 'Sim. Atendemos todo o estado de Pernambuco, incluindo Recife, Petrolina, Garanhuns, Arcoverde, Serra Talhada, Santa Cruz do Capibaribe, Bezerros, Gravatá e demais cidades do Agreste, Sertão e Zona da Mata.' },
  { q: 'É possível personalizar as mesas corporativas com a identidade visual da empresa?', a: 'Sim. As mesas Marzo Vitorino estão disponíveis em diversas cores, acabamentos e configurações. Nossa equipe de design auxilia na escolha dos materiais e cores que melhor se adequam à identidade visual da sua empresa.' },
];

export default function MesasPage() {
  useSEO({
    title: 'Mesas Corporativas em Caruaru PE | Marzo Vitorino',
    description: 'Mesas corporativas Marzo Vitorino em Caruaru PE. Estações de trabalho, mesas executivas e de reunião com design funcional, garantia de 5 anos e montagem inclusa em todo Pernambuco.',
    keywords: 'mesas corporativas Caruaru, Marzo Vitorino Pernambuco, estações de trabalho Recife, mesas executivas Agreste, mesas reunião PE, mesas escritório Nordeste',
    path: '/mesas',
    ogTitle: 'Mesas Corporativas em Caruaru PE | Marzo Vitorino',
    ogDescription: 'Mesas corporativas Marzo Vitorino em Caruaru PE. Estações de trabalho, mesas executivas e de reunião com montagem inclusa.',
    dateModified: '2026-04-27',
  });

  useEffect(() => {
    const schemaId = 'schema-mesas';
    let script = document.getElementById(schemaId) as HTMLScriptElement | null;
    if (!script) { script = document.createElement('script'); script.id = schemaId; script.type = 'application/ld+json'; document.head.appendChild(script); }
    const siteUrl = import.meta.env.VITE_SITE_URL ?? '';
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: 'Mesas Corporativas – Home Office Premium',
      description: 'Mesas corporativas em Caruaru PE. Estações de trabalho, mesas executivas e de reunião da marca Marzo Vitorino.',
      url: `${siteUrl}/mesas`,
    });

    const breadcrumbId = 'schema-mesas-breadcrumb';
    let breadcrumbScript = document.getElementById(breadcrumbId) as HTMLScriptElement | null;
    if (!breadcrumbScript) { breadcrumbScript = document.createElement('script'); breadcrumbScript.id = breadcrumbId; breadcrumbScript.type = 'application/ld+json'; document.head.appendChild(breadcrumbScript); }
    breadcrumbScript.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
        { '@type': 'ListItem', position: 2, name: 'Produtos', item: `${siteUrl}/produtos` },
        { '@type': 'ListItem', position: 3, name: 'Mesas Corporativas', item: `${siteUrl}/mesas` },
      ],
    });

    const itemListId = 'schema-mesas-itemlist';
    let itemListScript = document.getElementById(itemListId) as HTMLScriptElement | null;
    if (!itemListScript) { itemListScript = document.createElement('script'); itemListScript.id = itemListId; itemListScript.type = 'application/ld+json'; document.head.appendChild(itemListScript); }
    itemListScript.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: 'Mesas Corporativas Marzo Vitorino – Home Office Premium Caruaru',
      url: `${siteUrl}/mesas`,
      numberOfItems: desks.length,
      itemListElement: desks.map((desk, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'Product',
          name: desk.name,
          description: desk.description,
          image: desk.image,
          brand: { '@type': 'Brand', name: desk.brand },
          offers: {
            '@type': 'Offer',
            priceCurrency: 'BRL',
            price: desk.price.toFixed(2),
            availability: 'https://schema.org/InStock',
            seller: { '@type': 'Organization', name: 'Home Office Premium' },
            url: `${siteUrl}/contato`,
          },
        },
      })),
    });

    const faqId = 'schema-mesas-faq';
    let faqScript = document.getElementById(faqId) as HTMLScriptElement | null;
    if (!faqScript) { faqScript = document.createElement('script'); faqScript.id = faqId; faqScript.type = 'application/ld+json'; document.head.appendChild(faqScript); }
    faqScript.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    });

    return () => { script?.remove(); breadcrumbScript?.remove(); itemListScript?.remove(); faqScript?.remove(); };
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <PageHero
        eyebrow="Marzo Vitorino"
        eyebrowIcon="ri-layout-grid-line"
        title={
          <>
            Mesas corporativas com <span className="text-white/50 font-light">design funcional.</span>
          </>
        }
        subtitle="Estações de trabalho, mesas executivas e de reunião. Soluções completas para modernizar seu espaço em Caruaru e todo Pernambuco."
        image="/media/ia/0191.webp"
        alt="Mesas corporativas Marzo Vitorino em Caruaru PE"
        breadcrumb={[
          { label: 'Home', to: '/' },
          { label: 'Produtos', to: '/produtos' },
          { label: 'Mesas Corporativas' },
        ]}
        ctas={[
          { label: 'Solicitar orçamento', to: '/contato' },
          { label: 'Falar no WhatsApp', href: 'https://wa.me/5581982030285?text=Oi%2C%20tudo%20bem%3F%20Vim%20pelo%20site%20e%20gostaria%20de%20mais%20informa%C3%A7%C3%B5es', variant: 'secondary' },
        ]}
      />

      {/* Modelos */}
      <section className="bg-white">
        <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-20 md:py-28">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <SectionHeading
              eyebrow="Catálogo"
              title="Modelos disponíveis."
              subtitle={`${desks.length} mesas para diretores, reuniões e open offices.`}
            />
            <Link to="/marzo-vitorino" className="text-[13px] font-medium text-primary-600 hover:text-primary-700 whitespace-nowrap transition-colors cursor-pointer">
              Conhecer a marca Marzo Vitorino
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-5 gap-y-12" data-product-shop>
            {desks.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </div>
      </section>

      {/* Complete seu escritório */}
      <section className="bg-background-50">
        <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-20 md:py-28">
          <SectionHeading eyebrow="Continue explorando" title="Complete seu escritório corporativo." className="mb-10" />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {[
              { label: 'Cadeiras ergonômicas', sub: 'Frisokar · Plaxmetal', to: '/cadeiras', icon: 'ri-armchair-line' },
              { label: 'Divisórias modulares', sub: 'Sem obra', to: '/divisorias', icon: 'ri-layout-masonry-line' },
              { label: 'Mobiliário de aço', sub: 'W3 Indústria', to: '/aco', icon: 'ri-archive-line' },
            ].map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className="group flex items-center gap-4 bg-white rounded-xl border border-background-200/60 p-5 hover:border-primary-200/60 transition-all duration-300 cursor-pointer"
              >
                <span className="w-11 h-11 flex items-center justify-center rounded-lg bg-background-50 text-foreground-500 group-hover:text-primary-600 transition-colors flex-shrink-0">
                  <i className={`${item.icon} text-lg`}></i>
                </span>
                <span>
                  <span className="block text-foreground-900 font-medium text-[14px] group-hover:text-primary-600 transition-colors">{item.label}</span>
                  <span className="block text-foreground-400 text-[12px] font-light">{item.sub}</span>
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CuradoriaGaleria
        eyebrow="Curadoria de fabricante · Marzo Vitorino"
        titulo="Altis, Térios e Vio no detalhe."
        descricao="Mesas diretivas, salas de reunião e estações de trabalho retiradas dos e-books Altis/Térios e Vio. Cada item traz linha, aplicação e página de origem no catálogo."
        produtos={corporativo}
        contexto="mesas"
        tom="soft"
      />

      {/* FAQ */}
      <section className="bg-white">
        <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-20 md:py-28">
          <SectionHeading eyebrow="Dúvidas frequentes" title="FAQ — Mesas corporativas." className="mb-12" />
          <div className="max-w-3xl">
            {faqs.map((f) => (
              <FAQAccordion key={f.q} question={f.q} answer={f.a} />
            ))}
          </div>
        </div>
      </section>

      <CTABanner
        title="Montando seu escritório do zero?"
        subtitle="Montagem inclusa em todos os projetos e garantia de 5 anos. Fale com a gente e receba o orçamento."
        primaryLabel="Falar com um especialista"
        primaryHref="https://wa.me/5581982030285?text=Ol%C3%A1%20Ednilson!%20Vi%20o%20site%20da%20HomeOffice%20Premium.%20Gostaria%20de%20conversar%20sobre%20mesas%20corporativas."
      />

      <Footer />
      <WhatsAppFloat />
    </div>
  );
}